
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiUsers,
  FiUser,
  FiChevronRight,
  FiChevronDown,
  FiHome,
  FiGitBranch,
  FiCheckCircle,
  FiClock,
  FiAward,
} from "react-icons/fi";

import { getMyTeamList } from "../../store/slices/profile";

// Member card and recursive tree node
const TreeNode = ({
  node,
  expandedIds,
  toggleNode,
  isRoot = false,
}) => {
  const isExpanded = expandedIds.has(node._id);
  const hasChildren = node.children?.length > 0;

  return (
    <li className={`gi-tree-node ${isRoot ? "gi-root-node" : ""}`}>
      <div className={`gi-tree-card ${isRoot ? "gi-tree-root" : ""}`}>
        <div className="gi-tree-avatar">
          <FiUser />
        </div>

        <div className="gi-tree-info">
          <h5>{node.name || "Unknown User"}</h5>

          <span className="gi-tree-userid">
            {isRoot ? "My Account" : node.userId}
          </span>

          {!isRoot && (
            <div className="gi-tree-status-row">
              <span
                className={`gi-tree-status ${node.status === "ACTIVE" ? "active" : "pending"
                  }`}
              >
                {node.status === "ACTIVE" ? (
                  <FiCheckCircle />
                ) : (
                  <FiClock />
                )}
                {node.status || "PENDING"}
              </span>

              {node.isMatrixEligible && (
                <span className="gi-tree-eligible">
                  <FiAward />
                  Eligible
                </span>
              )}
            </div>
          )}
        </div>

        {hasChildren && (
          <button
            type="button"
            className="gi-tree-toggle"
            onClick={() => toggleNode(node._id)}
            title={isExpanded ? "Collapse referrals" : "Expand referrals"}
            aria-label={isExpanded ? "Collapse referrals" : "Expand referrals"}
          >
            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
            <span>{node.children.length}</span>
          </button>
        )}
      </div>

      {hasChildren && isExpanded && (
        <ul className="gi-tree-children">
          {node.children.map((child) => (
            <TreeNode
              key={child._id}
              node={child}
              expandedIds={expandedIds}
              toggleNode={toggleNode}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const MyTeam = () => {
  const dispatch = useDispatch();

  const {
    myReferralLevels = {},
    myReferralLevelsLoading,
    myTeamError,
  } = useSelector((state) => state.profile);

  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    dispatch(getMyTeamList());
  }, [dispatch]);

  const responseData = myReferralLevels;

  const levels = Array.isArray(responseData?.levels)
    ? responseData.levels
    : [];

  const totalTeam = responseData?.totalTeam || 0;
  const activeTeam = responseData?.activeTeam || 0;
  const totalLevels = responseData?.totalLevels || 0;

  // Flatten all members from all levels
  const allMembers = useMemo(() => {
    return levels.flatMap((level) =>
      (level.members || []).map((member) => ({
        ...member,
        level: level.level,
      }))
    );
  }, [levels]);

  // Build tree using referredBy._id
  const treeData = useMemo(() => {
    if (!allMembers.length) return null;

    const memberMap = new Map();
    const childrenMap = new Map();

    allMembers.forEach((member) => {
      memberMap.set(member._id, {
        ...member,
        children: [],
      });
    });

    allMembers.forEach((member) => {
      const parentId = member.referredBy?._id;

      if (!parentId) return;

      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }

      childrenMap.get(parentId).push(member._id);
    });

    // Get logged-in user from the first level member's referredBy
    const firstMember = allMembers.find(
      (member) => member.level === 1
    );

    const rootInfo = firstMember?.referredBy;

    if (!rootInfo?._id) return null;

    const buildNode = (id, visited = new Set()) => {
      if (visited.has(id)) return null;

      const member = memberMap.get(id);

      if (!member) return null;

      const nextVisited = new Set(visited);
      nextVisited.add(id);

      const childIds = childrenMap.get(id) || [];

      return {
        ...member,
        children: childIds
          .map((childId) => buildNode(childId, nextVisited))
          .filter(Boolean),
      };
    };

    const root = {
      _id: rootInfo._id,
      name: rootInfo.name || "My Account",
      userId: rootInfo.userId,
      children: (childrenMap.get(rootInfo._id) || [])
        .map((id) => buildNode(id))
        .filter(Boolean),
    };

    return root;
  }, [allMembers]);

  // Expand only the root initially
  useEffect(() => {
    if (!treeData) {
      setExpandedIds(new Set());
      return;
    }

    setExpandedIds(new Set([treeData._id]));
  }, [treeData]);

  // Expand or collapse individual member
  const toggleNode = (id) => {
    setExpandedIds((previous) => {
      const updated = new Set(previous);

      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }

      return updated;
    });
  };

  // Expand every member
  const expandAll = () => {
    if (!treeData) return;

    const ids = new Set();

    const collectIds = (node) => {
      if (!node) return;

      ids.add(node._id);
      node.children?.forEach(collectIds);
    };

    collectIds(treeData);
    setExpandedIds(ids);
  };

  // Collapse all members except root
  const collapseAll = () => {
    if (!treeData) return;

    setExpandedIds(new Set([treeData._id]));
  };

  return (
    <div className="gi-referral-page">
      {/* Breadcrumb */}
      <div className="gi-breadcrumb">
        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>My Network</span>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>My Team</span>
      </div>

      {/* Page Heading */}
      <div className="gi-team-heading">
        <div>
          <h3>My Team</h3>
          <p>
            Explore your referral network and team members
            across different levels.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="gi-team-summary">
        <div className="gi-team-summary-card">
          <div className="gi-summary-icon purple">
            <FiUsers />
          </div>
          <div>
            <span>Total Team</span>
            <h3>{totalTeam}</h3>
          </div>
        </div>

        <div className="gi-team-summary-card">
          <div className="gi-summary-icon green">
            <FiCheckCircle />
          </div>
          <div>
            <span>Active Members</span>
            <h3>{activeTeam}</h3>
          </div>
        </div>

        <div className="gi-team-summary-card">
          <div className="gi-summary-icon orange">
            <FiGitBranch />
          </div>
          <div>
            <span>Total Levels</span>
            <h3>{totalLevels}</h3>
          </div>
        </div>

        <div className="gi-team-summary-card">
          <div className="gi-summary-icon blue">
            <FiUser />
          </div>
          <div>
            <span>Inactive Members</span>
            <h3>{Math.max(totalTeam - activeTeam, 0)}</h3>
          </div>
        </div>
      </div>

      {/* Level Summary */}
      <div className="gi-level-summary">
        <div className="gi-level-heading">
          <div>
            <h4>Level Overview</h4>
            <p>Members distributed across your network</p>
          </div>
        </div>

        <div className="gi-level-list">
          {levels.map((level) => (
            <div className="gi-level-item" key={level.level}>
              <div className="gi-level-number">
                L{level.level}
              </div>

              <div className="gi-level-details">
                <strong>{level.totalMembers}</strong>
                <span>Members</span>
              </div>

              <div className="gi-level-active">
                <FiCheckCircle />
                {level.activeMembers} Active
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Referral Tree */}
      <section className="gi-referral-table-card gi-tree-section">
        <div className="gi-tree-header">
          <div className="p-3">
            <h4>
              <FiGitBranch />
              Referral Tree
            </h4>
            <p>
              Click on a member's arrow to view their referrals.
            </p>
          </div>


        </div>

        <div className="gi-tree-legend px-3">
          <span>
            <i className="active-dot" /> Active
          </span>

          <span>
            <i className="pending-dot" /> Pending
          </span>

          <span>
            <FiAward /> Matrix Eligible
          </span>
        </div>

        {myReferralLevelsLoading ? (
          <div className="gi-tree-message">
            Loading your team...
          </div>
        ) : myTeamError ? (
          <div className="gi-tree-message error">
            {myTeamError}
          </div>
        ) : !treeData ? (
          <div className="gi-tree-message">
            No referral members found.
          </div>
        ) : (
          <div className="gi-tree-scroll">
            <ul className="gi-tree">
              <TreeNode
                node={treeData}
                expandedIds={expandedIds}
                toggleNode={toggleNode}
                isRoot
              />
            </ul>
          </div>
        )}
      </section>
    </div>
  );
};

export default MyTeam;
